import { AccessLimitGateService } from '@modules/settings/services/access-limit-gate.service';
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
  untracked,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { slideAnimation } from '@modules/calendar-app/animations/slide-animation';
import { DateRangeCalendarEventsListStore } from '@modules/calendar-app/services/date-range-events-list.store';
import { EventsService } from '@modules/calendar-app/services/events.service';
import { CalendarAppStateService } from '@modules/calendar-app/services/state.service';
import { CalendarEvent } from '@modules/calendar-app/types/calendar-event';
import { CalendarType } from '@modules/calendar-app/types/calendar-type';
import {
  beginningOfPeriodInZone,
  calculateDateInZone,
  endOfPeriodInZone,
} from '@modules/calendar-app/utils/display-zone-period.util';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { fakeLocalToUtc } from '@modules/form-controls/types/timezone-convert';
import { type CalendarCellClickEvent } from '@modules/full-calendar/types/calendar-cell-click-event';
import { toDisplayZoneWallClock } from '@modules/full-calendar/types/display-timezone-format';
import { DragDataTypes } from '@modules/drag-n-drop/types/drag-data';
import type { DropdownSelectItem } from '@frontendlib/common/types/dropdown-select-item';
import { CalendarDropEvent } from '@modules/full-calendar/types/calendar-drop-event';
import { ModalService } from '@modules/modal/services/modal.service';
import { PopoverService } from '@frontendlib/components/features/popover/services/popover.service';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { isSameDay } from '@sharedlib/common/utils/date';
import type { CalendarEventSummaryDto } from '@sharedlib/dtos/dto/calendar-app/event.dto';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { resolveDropTimes } from '@modules/calendar-app/utils/drop-frame.util';

@Component({
  selector: 'stch-full-calendar',
  templateUrl: './full-calendar.component.html',
  styleUrls: ['./full-calendar.component.less'],
  animations: [slideAnimation],
  standalone: false,
})
export class FullCalendarComponent implements OnInit, OnDestroy {
  @ViewChild('popoverFormTemplate', { static: true })
  public readonly popoverFormTemplate: TemplateRef<void>;

  public calendarTypes: DropdownSelectItem<string>[] = [
    { title: 'Year', value: CalendarType.YEAR },
    { title: 'Month', value: CalendarType.MONTH },
    { title: 'Week', value: CalendarType.WEEK },
    { title: 'Work Week', value: CalendarType.WORKWEEK },
    { title: 'Day', value: CalendarType.DAY },
  ];
  public readonly calendarType = new FormControl<CalendarType>(null);
  public readonly date = new FormControl<Date>(null);
  protected selectedDate: Date;
  private skipNextScroll: boolean = false;
  /** Year view: the month just picked in the header, which the scroll goes to instead of today's month. */
  private pickedYearMonth: Date | null = null;
  public readonly CalendarType = CalendarType;
  private readonly alive = new Subject<void>();
  private readonly virtualEvent = signal<CalendarEvent>(null);
  protected readonly popoverClose = new Subject<void>();
  private readonly popoverVisibility = new Subject<boolean>();

  protected readonly eventsService = inject(EventsService);
  protected readonly calendarAppStateService = inject(CalendarAppStateService);
  protected readonly popoverService = inject(PopoverService);
  protected readonly injector = inject(Injector);
  protected readonly modalService = inject(ModalService);
  protected readonly dateRangeCalendarEventsListStore = inject(DateRangeCalendarEventsListStore);
  protected readonly advancedSearchService = inject(AdvancedSearchService);
  protected readonly LOCALE_ID = inject(LOCALE_ID);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  constructor() {
    // The grid is pure chrome: it must render on the FIRST pass, never wait for async state. The store
    // seeds `selectedDate` synchronously (today), so seed the header control and the selection marker
    // from it here — the effects below only keep them in step afterwards. Waiting for the first effect
    // flush left the 3rd panel empty for a tick, and empty for good when upstream state never settled.
    const initialDate = this.dateRangeCalendarEventsListStore.selectedDate();
    this.date.setValue(initialDate, { emitEvent: false });
    this.selectedDate = initialDate;
  }

  /**
   * SBUX-829: the caption's 'monthYear' / 'weekNumber' formats read browser-local getters, while
   * `date.value` is now a display-zone instant. Feeding them the account wall clock keeps the
   * heading naming the same month/week the grid below it is actually showing.
   */
  /**
   * The day the user last acted on, expressed in the display frame the grids compare in.
   *
   * The grids lay out account-zone-midnight instants, so the marker has to be reckoned in the account
   * zone too — a browser-local comparison rings the neighbouring cell whenever the account zone
   * differs from the device's.
   */
  protected get selectedDayInDisplayFrame(): Date | null {
    return this.selectedDate ? toDisplayZoneWallClock(this.selectedDate, this.displayTimeZoneService.timeZone()) : null;
  }

  /**
   * Remember which day was clicked so the grid can mark it. The store's `selectedDate` snaps to the
   * START of the displayed period on navigation, so it cannot serve as "the day I clicked" — this is
   * tracked separately, additively: the existing click behaviour (scroll to the day, or open the
   * create popover) is unchanged.
   */
  protected handleDayClicked(event: CalendarCellClickEvent): void {
    this.selectedDate = event.date;
    if (this.hasEventsOnDate(event.date)) {
      this.scrollToDateHandler(event.date);
    } else {
      this.handleDynamicPopoverEventCreation(event.date, event.originRef);
    }
  }

  protected get captionDate(): Date | null {
    const value = this.date.value;
    return value ? toDisplayZoneWallClock(value, this.displayTimeZoneService.timeZone()) : null;
  }

  /**
   * The day whose week the header badge numbers, as an account wall clock: the period on show, or today
   * when Month / Year view is on the current month (its first day would name last week).
   */
  protected get weekBadgeDate(): Date | null {
    const caption = this.captionDate;
    if (!caption || !(this.calendarType.value === CalendarType.MONTH || this.calendarType.value === CalendarType.YEAR)) {
      return caption;
    }

    const today = toDisplayZoneWallClock(new Date(), this.displayTimeZoneService.timeZone());
    const sameMonth = today.getFullYear() === caption.getFullYear() && today.getMonth() === caption.getMonth();
    return sameMonth ? today : caption;
  }

  private readonly _selectedDateEffect = effect(() => {
    const selectedDate = this.dateRangeCalendarEventsListStore.selectedDate();
    this.date.setValue(selectedDate, { emitEvent: false });
    this.selectedDate = selectedDate;
  });

  public readonly events = computed((): CalendarEventSummaryDto[] => {
    const { items } = this.dateRangeCalendarEventsListStore.events();
    const virtualEvent = this.virtualEvent();
    return virtualEvent ? [...items, virtualEvent] : items;
  });

  protected readonly calendarScrollTarget = signal<Date | null>(null);

  private readonly _selectedDateScrollEffect = effect(() => {
    const selectedDate = this.dateRangeCalendarEventsListStore.selectedDate();
    untracked(() => {
      const picked = this.pickedYearMonth;
      this.pickedYearMonth = null;
      this.calendarScrollTarget.set(picked ?? this.computeNavigationScrollTarget(selectedDate, this.calendarType.value));
    });
  });

  private readonly _storeScrollEffect = effect(() => {
    const storeDate = this.dateRangeCalendarEventsListStore.scrollToDate();
    if (storeDate) {
      if (this.skipNextScroll) {
        this.skipNextScroll = false;
        return;
      }
      untracked(() => this.calendarScrollTarget.set(storeDate));
    }
  });

  public ngOnInit() {
    this.calendarAppStateService.calendarType
      .get()
      .pipe(takeUntil(this.alive))
      .subscribe((type) => {
        this.calendarType.setValue(type);
      });

    this.calendarType.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.alive)).subscribe((type) => {
      this.calendarAppStateService.calendarType.set(type);
      this.calendarScrollTarget.set(this.computeNavigationScrollTarget(this.date.value, type));
    });

    // `valueChanges` fires ONLY for user edits — the store→control direction below uses
    // `{ emitEvent: false }` — so every value arriving here comes from the header's mini date-picker,
    // which emits a display-zone WALL CLOCK (`calendar-month-small-di`). The store computes periods with
    // `beginningOfPeriodInZone`, which projects a REAL INSTANT, so handing it the carrier converted the
    // day twice and landed on the neighbour: picking Mar 25 opened Mar 24 (and the day AFTER for a
    // browser west of the account). Resolve to the instant first, and compare in that same frame so the
    // no-change filter is not comparing a carrier against an instant (SBUX-829).
    this.date.valueChanges
      .pipe(
        map((date) => fakeLocalToUtc(date, this.displayTimeZoneService.timeZone())),
        // Year view: the month already selected, picked again (after a view switch scrolled to today's
        // month), changes no date, so the filter below drops it; scroll to it here (PR #2873 review, MD-01).
        tap((date) => {
          if (
            this.calendarType.value === CalendarType.YEAR &&
            date.getTime() === this.dateRangeCalendarEventsListStore.selectedDate().getTime()
          ) {
            this.calendarScrollTarget.set(date);
          }
        }),
        filter((date) => date.getTime() !== this.dateRangeCalendarEventsListStore.selectedDate().getTime()),
        takeUntil(this.alive),
      )
      .subscribe((date) => {
        // Inside the current year the scroll went to today's month, so an earlier month never came into
        // view (Johanna, 2026-11-02); go to the month that was picked.
        this.pickedYearMonth = this.calendarType.value === CalendarType.YEAR ? date : null;
        this.dateRangeCalendarEventsListStore.setSelectedDate(date);
      });

    this.popoverVisibility
      .pipe(
        filter((visible) => !visible),
        takeUntil(this.alive),
      )
      .subscribe(() => this.virtualEvent.set(null));

    this.calendarAppStateService
      .getNavigateToDate()
      .pipe(takeUntil(this.alive))
      .subscribe((date) => {
        this.date.setValue(date);
      });
  }

  public ngOnDestroy() {
    this.popoverClose.next();
    this.popoverClose.complete();
    this.alive.next();
    this.alive.complete();
  }

  /**
   * SBUX-829 — EVERY grid emits the same kind of value: an account-zone wall clock (a "fake-local"
   * `Date` whose browser-local getters read the intended account day and time).
   *
   * It used to depend on the grid family — month/year wall clocks, week/day instants — and consumers had
   * to ask which. That split could not survive: `eventDropped` is a single output, and the week grid
   * raised it from two handlers in two different frames, so no answer here was right for both. Uniform
   * is the only contract a single consumer can honour, and the account zone is the right one because
   * that is the zone every grid is LAID OUT in (see `createDisplayTimeZoneDateAdapter`).
   *
   * Kept as a named constant rather than inlined so `resolveDropTimes` still reads as a decision about
   * frames, and so a future grid that emits instants has one obvious place to say so.
   */
  private get emitsAccountDayWallClock(): boolean {
    return true;
  }

  /** The acted-on cell as an account-day wall clock, whichever grid emitted it. */
  private toAccountDayWallClock(date: Date): Date {
    return this.emitsAccountDayWallClock ? date : toDisplayZoneWallClock(date, this.displayTimeZoneService.timeZone());
  }

  /**
   * SBUX-829 — the clicked cell in the frame the event's write path needs, which depends on the kind of
   * event the grid creates: month/year cells create ALL-DAY events (a floating calendar date, stored at
   * UTC midnight), week/day cells create TIMED ones (a real instant).
   *
   * This is the CREATE counterpart of the drop path's `resolveDropTimes`, and it deliberately reuses it —
   * it is the same question about the same emitted value. Without it the emitted wall clock went into
   * `event.startTime` as though it were an instant, and the form then converted it a SECOND time into
   * its own zone: clicking midnight on Thu 16 with the account in Kyiv and the calendar in Almaty seeded
   * the form at 23:00 on Wed 15, which ticking All Day turned into a two-day event. Reproduced live.
   */
  private resolveCellDate(date: Date): Date {
    const allDay = this.calendarType.value === CalendarType.MONTH || this.calendarType.value === CalendarType.YEAR;
    // An ALL-DAY draft keeps the account-day wall clock. That is the same shape `@TransformAllDayDate`
    // gives a decoded all-day event, so the form seeds and saves a draft and a stored event identically —
    // and `seedSummaryControlsForZone` deliberately skips all-day for exactly that reason.
    // A TIMED draft must become the real instant, because the form re-expresses an instant in its own zone.
    return allDay ? date : this.toInstant(date);
  }

  /** The acted-on cell as a real instant, whichever grid emitted it. */
  private toInstant(date: Date): Date {
    return this.emitsAccountDayWallClock ? fakeLocalToUtc(date, this.displayTimeZoneService.timeZone()) : date;
  }

  protected handleDynamicPopoverEventCreation(date: Date, originRef: ElementRef<unknown>): void {
    this.selectedDate = date;

    this.calendarAppStateService
      .getBestSelectedCalendarUid()
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((calendarUid) => {
        const calendarEvent = CalendarEvent.fromCalendarCell(null, this.resolveCellDate(date), this.calendarType.value);
        calendarEvent.calendarId = calendarUid;
        this.virtualEvent.set(calendarEvent);
        this.openPopoverForm(originRef, calendarEvent);
      });
  }

  protected handleEventModalCreation(date: Date): void {
    this.dropQuickCreate();
    this.calendarAppStateService
      .getBestSelectedCalendarUid()
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((calendarUid) => {
        const calendarEvent = CalendarEvent.fromCalendarCell(null, this.resolveCellDate(date), this.calendarType.value);
        calendarEvent.calendarId = calendarUid;
        this.modalService.openFullForm(calendarEvent, this.injector);
      });
  }

  protected handleCellDrop(event: CalendarDropEvent): void {
    this.calendarAppStateService
      .getBestSelectedCalendarUid()
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((calendarUid) => {
        if (DragDataTypes.event === event.dragData.type) {
          // Filter out remote events — their times are organizer-controlled.
          // Inside DragDataTypes.event branch, data items are CalendarEventSummaryDto.
          const eventSummaries = event.dragData.data as CalendarEventSummaryDto[];
          const editableEvents = eventSummaries.filter((e) => !CalendarEvent.isRemoteSummary(e));
          if (editableEvents.length > 0) {
            // SBUX-829: all-day events are date-only (ICS VALUE=DATE) — persist the dropped day as
            // UTC-midnight so it stays that calendar date in every timezone. A TIMED event keeps a
            // real instant, but the month/year grids emit `beginningOfDay`/`endOfDay` of an
            // account-day WALL CLOCK, so that value has to be resolved through the account zone
            // before it can be stored as one: used verbatim it recorded the browser's midnight, which
            // made the stored instant depend on the device — the very thing this ticket removes.
            const allDay = editableEvents.every((e) => e.allDay);
            // Resolved ONCE, before anything compares or persists them, so the no-op check below and
            // the write below cannot disagree about which frame they are in. `resolveDropTimes` owns
            // all four grid×event-kind combinations — see its TSDoc for why each is what it is.
            const { start: newStart, end: newEnd } = resolveDropTimes(
              { start: event.newStart, end: event.newEnd },
              {
                allDay,
                emitsAccountDayWallClock: this.emitsAccountDayWallClock,
                timeZone: this.displayTimeZoneService.timeZone(),
              },
            );

            // A drop that lands on the same slot changes nothing — skip it. This matters
            // for recurring occurrences: bunchUpdate applies THIS_EVENT semantics, which
            // would materialize the occurrence and append a spurious EXDATE to the parent
            // (SBUX-761) even though the event never moved. All-day summaries carry their
            // start/end at (local) midnight while the grid emits end-of-day for the drop
            // end, so all-day events are compared by calendar day rather than exact ms.
            //
            // Comparing the RESOLVED values matters for the timed case: against the raw emitted
            // wall clock, every month/year drop looked like a move whenever the account zone differed
            // from the browser's, so this guard silently stopped guarding — and a dropped recurring
            // occurrence got materialized with a spurious EXDATE after all.
            const timeChanged = editableEvents.some((e) => {
              if (e.allDay) {
                // Both sides are UTC-midnight carriers, so a browser-local day comparison agrees.
                return (
                  (newStart !== undefined && !isSameDay(new Date(e.startTime), newStart)) ||
                  (newEnd !== undefined && !isSameDay(new Date(e.endTime), newEnd))
                );
              }
              return (
                (newStart !== undefined && new Date(e.startTime).getTime() !== newStart.getTime()) ||
                (newEnd !== undefined && new Date(e.endTime).getTime() !== newEnd.getTime())
              );
            });
            if (!timeChanged) {
              return;
            }

            for (const editableEvent of editableEvents) {
              editableEvent.startTime = newStart ?? editableEvent.startTime;
              editableEvent.endTime = newEnd ?? editableEvent.endTime;
            }

            this.eventsService.bunchUpdate(
              { ids: editableEvents.map(({ id }) => id) },
              {
                startTime: newStart,
                endTime: newEnd,
              },
              { tenantItems: editableEvents },
            );
          }

          return;
        }

        if (!event.originRef) {
          return;
        }

        const calendarEvent = CalendarEvent.fromCalendarCell(
          CalendarEvent.fromDragData(event.dragData),
          // Same resolution as the click paths — this value came from a grid too (SBUX-829).
          this.resolveCellDate(event.newStart),
          this.calendarType.value,
        );
        calendarEvent.calendarId = calendarUid;
        this.virtualEvent.set(calendarEvent);
        this.openPopoverForm(event.originRef, calendarEvent);
      });
  }

  protected openDailyView(date: Date): void {
    this.dropQuickCreate();
    // Resolve the instant BEFORE switching type: `date` was emitted by the grid we are leaving, and
    // `toInstant` interprets it according to the CURRENT calendar type. Switching first made it read
    // the value as if the day grid had emitted an instant, silently skipping the conversion.
    const instant = this.toInstant(date);
    this.calendarAppStateService.calendarType.set(CalendarType.DAY);
    // `setDate` drives the grid viewDate AND the events query range, both of which want an instant.
    this.setDate(instant);
  }

  protected scrollToDateHandler(date: Date): void {
    this.skipNextScroll = true;
    // The events list projects this into the display zone itself, so it must receive a real instant —
    // handing it an account-day wall clock projected it a second time.
    this.dateRangeCalendarEventsListStore.setScrollToDate(this.toInstant(date));
  }

  protected setToday(): void {
    const currentDate = new Date();
    if (!isSameDay(this.date.value, currentDate)) {
      this.setDate(currentDate);
    }

    this.dateRangeCalendarEventsListStore.setScrollToDate(currentDate);
  }

  protected hasEventsOnDate(date: Date): boolean {
    // Compare in ONE frame: the cell's account day against each event's boundaries in the same frame.
    // A timed boundary is a real instant and is projected; an all-day boundary is already a
    // local-midnight carrier and must not be (SBUX-829).
    const zone = this.displayTimeZoneService.timeZone();
    const cellDay = this.toAccountDayWallClock(date);
    return this.events().some((event) => {
      const start = event.allDay ? event.startTime : toDisplayZoneWallClock(event.startTime, zone);
      const end = event.allDay ? event.endTime : toDisplayZoneWallClock(event.endTime, zone);
      return (
        (cellDay.getTime() >= start.getTime() && cellDay.getTime() <= end.getTime()) ||
        isSameDay(cellDay, start) ||
        isSameDay(cellDay, end)
      );
    });
  }

  protected nextDate(): void {
    this.setDate(this.stepDate(1));
  }

  protected prevDate(): void {
    this.setDate(this.stepDate(-1));
  }

  // SBUX-829 N1: step to the start of the adjacent period in the DISPLAY (account) zone, so the
  // selected date lands on the account-zone period boundary and the grid (adapter) and events-list
  // (store range) resolve the same week — instead of diverging at boundaries when account ≠ browser.
  private stepDate(direction: 1 | -1): Date {
    const timeZone = this.displayTimeZoneService.timeZone();
    const stepped = calculateDateInZone(this.date.value, direction, this.calendarType.value, timeZone);
    return beginningOfPeriodInZone(stepped, this.calendarType.value, this.LOCALE_ID, timeZone);
  }

  protected openEventInModal(summary: CalendarEventSummaryDto): void {
    this.eventsService
      .getItem(summary.id)
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((event) => {
        this.modalService.openFullForm(event, this.injector);
      });
  }

  private computeNavigationScrollTarget(date: Date, calendarType: CalendarType): Date {
    const timeZone = this.displayTimeZoneService.timeZone();
    const fromTime = beginningOfPeriodInZone(date, calendarType, this.LOCALE_ID, timeZone);
    const toTime = endOfPeriodInZone(date, calendarType, this.LOCALE_ID, timeZone);
    const today = new Date();
    if (fromTime.getTime() <= today.getTime() && toTime.getTime() >= today.getTime()) {
      return today;
    }
    return new Date(fromTime);
  }

  /**
   * SBUX-829 — programmatic navigation: the period chevrons, "Today", and opening a day from a cell.
   *
   * Every caller here already produces a REAL INSTANT (`stepDate` ends in `beginningOfPeriodInZone`,
   * `setToday` uses `new Date()`, `openDailyView` resolves with `toInstant`). The `date.valueChanges`
   * pipeline converts carrier→instant because the header's mini date-picker emits a display-zone WALL
   * CLOCK — so emitting from here ran an already-resolved instant through that conversion a SECOND time,
   * shifting it by (browser − account).
   *
   * That is why the week chevron advanced only on every OTHER click west of the account: a week start of
   * Sun 00:00 Kyiv (Sat 21:00Z) came out as Sat 17:00 Kyiv, so the next step snapped back to the week
   * already shown. East of the account the same shift stayed inside Sunday, which is why Bishkek looked
   * fine and New York did not. Reported from the running app; reproduced with a 4-click trace.
   *
   * So: write the control WITHOUT emitting and push the instant straight to the store. The store→control
   * effect already uses `{ emitEvent: false }` in the other direction, so this keeps one convention —
   * `valueChanges` carries user edits from the picker only.
   */
  private setDate(newDate: Date): void {
    this.date.setValue(newDate, { emitEvent: false });
    const current = this.dateRangeCalendarEventsListStore.selectedDate();
    if (!current || current.getTime() !== newDate.getTime()) {
      this.dateRangeCalendarEventsListStore.setSelectedDate(newDate);
    }
  }

  /**
   * A click on an empty slot or day opens the quick-create popover at once (it no longer waits to see whether a
   * double-click follows). The double-click's own action — the full new-event window, or the Day view — replaces
   * that popover, so it closes it and its placeholder event first instead of leaving them behind.
   */
  private dropQuickCreate(): void {
    this.popoverClose.next();
    this.virtualEvent.set(null);
  }

  private openPopoverForm(originRef: ElementRef<unknown>, calendarEvent: CalendarEvent): void {
    // A limited account gets the "paused" popup instead of the form of a new event; the placeholder goes with it.
    if (!calendarEvent.id && this.injector.get(AccessLimitGateService).blocks('createEvent', this.injector)) {
      this.virtualEvent.set(null);
      return;
    }
    this.popoverService.create(originRef, {
      template: this.popoverFormTemplate,
      placement: 'bottomLeft',
      trigger: 'click',
      showUntil: this.popoverClose,
      injector: this.injector,
      visibleChange: this.popoverVisibility,
      context: { calendarEvent: calendarEvent },
    });
  }
}
